import { memo, useCallback, useState } from 'react';

import { HStack, Stack } from '@chakra-ui/react';

import { Drawer } from '@/components/common';
import { AgentThinkingHint, OnAgentThinking } from '@/components/core';
import { AgentChatPersona } from '@/components/features/ChatComponent/ui/AgentChatPersona';
import { CustomLangflowChat } from '@/components/features/ChatComponent/ui/CustomLangflowChat';
import { ChatMenu } from '@/components/features/ChatMenu';
import { AgentName } from '@/constant/agents';
import { useChatSessions } from '@/hooks/useChatSessions';
import { useAgents } from '@/store/agents';
import { useUser } from '@/store/auth';
import { ChatType, useChats } from '@/store/chats';
import { Session } from '@/types/chat';

export const GlobalConnieChat = memo(function GlobalConnieChat() {
  const { isOpen, sessionId } = useChats(state => state.chatState.connie);
  const updateChatState = useChats(state => state.updateChatState);
  const { user } = useUser();
  const { data: agent } = useAgents(AgentName.CONNIE);
  const {
    sessions,
    isSessionsLoading,
    setCurrentSession,
    startNewChat,
    handleAfterMessageSent,
  } = useChatSessions({
    chatType: ChatType.connie,
    userId: user?.id.toString(),
    enabled: isOpen,
  });
  const [isSessionsMode, setIsSessionsMode] = useState(false);
  const [isChatThinking, setIsChatThinking] = useState(false);
  const [thinkingHint, setThinkingHint] = useState<AgentThinkingHint>({});

  const handleThinking = useCallback<OnAgentThinking>((isThinking, hint) => {
    setIsChatThinking(isThinking);
    const next = isThinking ? (hint ?? {}) : {};
    // Called on every streamed token; only a new phase should re-render.
    setThinkingHint(current =>
      current.status === next.status && current.toolName === next.toolName
        ? current
        : next
    );
  }, []);

  const handleClose = useCallback(() => {
    setIsSessionsMode(false);
    setIsChatThinking(false);
    setThinkingHint({});
    updateChatState(ChatType.connie, { isOpen: false });
  }, [updateChatState]);

  const handleNewChat = useCallback(() => {
    setIsSessionsMode(false);
    startNewChat();
  }, [startNewChat]);

  const handleSessionChange = useCallback(
    (session: Session) => {
      setCurrentSession(session);
      setIsSessionsMode(false);
    },
    [setCurrentSession]
  );

  const handleReturnToChat = useCallback(() => {
    setIsSessionsMode(false);
  }, []);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size='lg'
      contentProps={{
        maxWidth: { base: '100%', xl: '720px' },
        'aria-label': 'Ask Connie',
      }}
    >
      <Stack height='full' minH={0} gap={0} overflow='hidden'>
        <HStack
          px={{ base: 4, md: 6 }}
          py={{ base: 2, md: 3 }}
          justifyContent='space-between'
          flexShrink={0}
          borderBottom='1px solid'
          borderColor='border.light'
        >
          <AgentChatPersona
            name={agent?.name ?? 'Connie'}
            src={agent?.avatar?.url}
            isThinking={isChatThinking}
            status={thinkingHint.status}
            toolName={thinkingHint.toolName}
          />
          <ChatMenu
            currentSessionId={sessionId}
            isChatThinking={isChatThinking}
            isSessionsMode={isSessionsMode}
            isSessionsEnabled={sessions.length > 0}
            isSessionsLoading={isSessionsLoading}
            onNewChatClick={handleNewChat}
            onSessionChangeClick={() => setIsSessionsMode(mode => !mode)}
            onClose={handleClose}
            closeLabel='Close Ask Connie'
          />
        </HStack>
        <CustomLangflowChat
          chatType={ChatType.connie}
          isActive={isOpen}
          sessions={sessions}
          isSessionsMode={isSessionsMode}
          sessionId={sessionId ?? undefined}
          user={user}
          onMessage={handleAfterMessageSent}
          onThinking={handleThinking}
          onSessionChangeClick={handleSessionChange}
          onReturnToChatClick={handleReturnToChat}
        />
      </Stack>
    </Drawer>
  );
});
